import { DashboardDeviceLabel } from 'App/dashboards/_components/DashboardDeviceLabel'
import {
  DashboardGroupedTable,
  type TGroupedTableColumn,
} from 'App/dashboards/_components/DashboardGroupedTable'
import { formatApplicationRate } from 'App/dashboards/_utils/applicationRate'
import { getCropColumn } from 'App/dashboards/_utils/getCropColumn'
import {
  usePressureSensorActivity,
  type TPressureSensorRow,
} from 'App/dashboards/_utils/usePressureSensorActivity'
import { withCropNames, type TZoneCrops } from 'App/dashboards/_utils/zoneCrops'
import { translate } from 'i18n/i18n'
import type moment from 'moment-timezone'
import { useEffect, useMemo } from 'react'
import { mapApiEmitterTypeToLabel } from 'utils/mapApiEmitterTypeToLabel'
import { minutesToHoursAndMinutes } from 'utils/minutesToHoursAndMinutes'
import { unitConverter } from 'utils/unitConverter/unitConverter'

// Empty string for the CSV value (CSV blank = missing); the table renders a dash instead.
const formatVolume = (mm: number | null): string =>
  mm === null ? '' : unitConverter.irrigationActivity(mm).valueWithSuffix()

const formatDuration = (ms: number | null): string =>
  ms === null ? '' : minutesToHoursAndMinutes(ms / 60000)

type TPressureSensorRowWithCrop = TPressureSensorRow & { cropName: string | null }

export const IrrigationActivityByPressureSensors = ({
  propertyIds,
  dateFrom,
  dateTo,
  zoneCrops,
  refreshToken,
  onLoaded,
}: {
  propertyIds: number[]
  dateFrom: moment.Moment
  dateTo: moment.Moment
  zoneCrops: TZoneCrops
  refreshToken?: number
  // Reports the widget's last successful fetch time up to the shell's "Last Updated".
  onLoaded?: (date: Date) => void
}) => {
  const { sections, loading, error, lastFetchedAt } = usePressureSensorActivity({
    propertyIds,
    dateFrom,
    dateTo,
    refreshToken,
  })

  useEffect(() => {
    if (lastFetchedAt) onLoaded?.(lastFetchedAt)
  }, [lastFetchedAt, onLoaded])

  const showCrops = sections.some((section) => zoneCrops.multiCropPropertyIds.has(section.propertyId))

  const sectionsWithCrops = useMemo(
    () => withCropNames(sections, (row) => row.zoneIds, zoneCrops, showCrops),
    [sections, zoneCrops, showCrops],
  )

  const columns = useMemo<TGroupedTableColumn<TPressureSensorRowWithCrop>[]>(
    () => [
      ...(showCrops ? [getCropColumn<TPressureSensorRowWithCrop>()] : []),
      {
        key: 'zone',
        header: translate.phrases.banyanApp('Zone'),
        value: (row) => row.zoneName ?? '',
        render: (row) => row.zoneName ?? '-',
        // Group column: blanks on repeat so each zone's PTs sit together under one label.
        collapseRepeats: true,
      },
      {
        key: 'emitter',
        header: translate.phrases.banyanApp('Emitter'),
        value: (row) => mapApiEmitterTypeToLabel(row.emitterType),
      },
      {
        key: 'device',
        header: translate.phrases.banyanApp('Device'),
        value: (row) => row.deviceName,
        render: (row) => (
          <DashboardDeviceLabel name={row.deviceName} externalVendorNames={row.externalVendorNames} />
        ),
      },
      {
        key: 'applicationRate',
        header: translate.phrases.banyanApp('Application Rate'),
        value: (row) => formatApplicationRate(row.applicationRate),
        render: (row) => formatApplicationRate(row.applicationRate) || '-',
      },
      {
        key: 'volume',
        header: translate.phrases.banyanApp('Total Volume'),
        value: (row) => formatVolume(row.volumeMm),
        render: (row) => formatVolume(row.volumeMm) || '-',
      },
      {
        key: 'duration',
        header: translate.phrases.banyanApp('On Duration'),
        value: (row) => formatDuration(row.durationMs),
        render: (row) => formatDuration(row.durationMs) || '-',
      },
    ],
    [showCrops],
  )

  return (
    <DashboardGroupedTable
      title={translate.phrases.banyanApp('Irrigation Activity by Pressure Sensors')}
      sections={sectionsWithCrops}
      columns={columns}
      loading={loading}
      error={error}
      downloadFileName={translate.phrases.banyanApp('Irrigation Activity by Pressure Sensors')}
    />
  )
}
